/**
 * Web component: Campaign teaser (campaign-teaser)
 * -----------------------------------------------------------------------------
 * Campaign Teaser.
 *
 */
import classes from '~/config/classes';
import './campaign-teaser.scss';

import { vueReady } from '~/core/runners';

import { dateTime } from '~/helpers/general';

import {
  checkIfActiveNonPrivate,
  handleTrackingCampaign,
  updateTeaser,
} from '~/helpers/teasers';

import useDiscountCampaignsStore from '~stores/discount-campaigns';
import useOverlaysStore from '~stores/overlays';

let discountCampaignsStore;
let overlaysStore;

/**
 * DOM selectors.
 */
const selectors = {
  campaignContainer: '[js-campaign-teaser="content"]',
  countdownInner: '[js-campaign-teaser="countdownInner"]',
  countdownOuter: '[js-campaign-teaser="countdownOuter"]',
  contentDefault: '[js-campaign-teaser="contentDefault"]',
  copyButton: '[js-campaign-teaser="copyButton"]',
  promoText: '[js-campaign-teaser="promoText"]',
  notificationText: '[js-data-notification="overlaytext"]',
};

/**
 * Create new <campaign-teaser> custom element.
 */
class CampaignTeaser extends HTMLElement {
  data(component) {
    const campaignContainer = component.querySelector(
      selectors.campaignContainer,
    );
    if (campaignContainer) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleTrackingCampaign(
              campaignContainer,
              'Campaign Teaser',
              'view_promotion',
            );
            observer.disconnect();
          }
        });
      });

      observer.observe(campaignContainer);
    }

    return {
      selectors: {
        campaignContainer,
        copyButton: component.querySelector(selectors.copyButton),
        promoText: component.querySelector(selectors.promoText),
        notificationText: document.querySelector(selectors.notificationText),
      },
    };
  }

  constructor() {
    super();
    this.$data = this.data(this);
    this.teaserUpdated = false;
  }

  /**
   * Component lifecycle - mounted.
   */
  connectedCallback() {
    this.$data = Object.keys(this.$data).length ? this.$data : this.data(this);

    const { copyButton } = this.$data.selectors;

    if (copyButton) {
      copyButton.addEventListener('click', (event) => {
        this.applyCode(event);
      });
    }

    // Subscribe to store changes to handle campaign updates
    if (discountCampaignsStore) {
      let previousActive = discountCampaignsStore.active;

      if (previousActive) {
        // Initialize teaser state based on current active campaign
        updateTeaser.call(this, { active: previousActive });
        this.updateText({ active: previousActive });
      }

      this.storeUnsubscribe = discountCampaignsStore.$subscribe(
        (mutation, state) => {
          // Only update teaser when active state actually changes
          if (state.active !== previousActive) {
            updateTeaser.call(this, { active: state.active });
            this.updateText({ active: state.active });
            previousActive = state.active;
          }
        },
      );
    }

    this.handleCountdown();
  }

  updateText(state) {
    const { copyButton, promoText } = this.$data.selectors;

    const isActiveNonPrivate = checkIfActiveNonPrivate({
      active: state.active,
    });

    if (isActiveNonPrivate) {
      // Code is activated - show promo text and button with Code text
      if (promoText) promoText.classList.remove(classes.hidden);
      if (copyButton) {
        copyButton.classList.remove(classes.hidden);
        const copyButtonText = copyButton.querySelector('span:first-child');
        if (copyButtonText) {
          copyButtonText.innerText = state.active.code[0];
        }
        copyButton.disabled = true;
      }
    } else {
      // Code is removed or private - show button, hide promo text
      if (promoText) promoText.classList.add(classes.hidden);
      if (copyButton) copyButton.classList.remove(classes.hidden);
      const copyButtonText = copyButton.querySelector('span:first-child');

      if (copyButton?.dataset?.text) {
        copyButtonText.innerText = copyButton.dataset.text;
      }
      copyButton.disabled = false;
    }
  }

  handleCountdown() {
    const createCountdown = (startTime, endTime, onExpire) => {
      let result = dateTime(startTime, endTime);

      if (result) {
        const countdownInterval = setInterval(() => {
          result = dateTime(startTime, endTime);
          if (!result) {
            onExpire();
            this.classList.remove(classes.active);
            clearInterval(countdownInterval);
          }
        }, 1000);
      }
    };

    // inner countdown
    const countdownInner = this.querySelector(selectors.countdownInner);
    const contentDefault = this.querySelector(selectors.contentDefault);
    createCountdown(
      this.dataset.countdownInnerStart,
      this.dataset.countdownInnerEnd,
      () => {
        countdownInner.classList.add(classes.hidden);
        contentDefault.classList.remove(classes.hidden);
        cnvs.EventBus.emit('campaign:countdown-finished');
      },
    );

    // outer countdown
    const countdownOuter = this.querySelector(selectors.countdownOuter);
    const campaignContainer = this.querySelector(selectors.campaignContainer);
    createCountdown(
      Number(this.dataset.countdownOuterStart),
      this.dataset.countdownOuterEnd,
      () => {
        campaignContainer.classList.remove('countdown-outer');
        countdownOuter.classList.add(classes.hidden);
      },
    );
  }

  /**
   * Component lifecycle - unmounted.
   */
  disconnectedCallback() {
    // Clean up store subscription
    if (this.storeUnsubscribe) {
      this.storeUnsubscribe();
    }
  }

  /**
   * Apply code from teaser button
   * @param {Event} event
   */
  applyCode(event) {
    // Get the code from the button's data attribute
    const code = event.currentTarget.dataset.code;

    if (!code) {
      return;
    }

    if (!discountCampaignsStore || !overlaysStore) {
      return;
    }

    const { copyButton, notificationText, promoText } = this.$data.selectors;

    const copyButtonText = copyButton.querySelector('span:first-child');

    discountCampaignsStore.setEnteredCode(code.toUpperCase());

    // Listen for code state changes to open overlay when ready
    const unsubscribe = discountCampaignsStore.$subscribe((mutation, state) => {
      if (state.codeState === 'loading') {
        if (copyButtonText)
          copyButtonText.innerText =
            cnvs.strings.accessibility.loading || 'Loading...';
      } else if (state.codeState === 'valid') {
        notificationText.innerHTML =
          cnvs.strings.discount_campaigns.notification_text;

        updateTeaser.call(this, {
          active: discountCampaignsStore.active,
        });

        // Reset code state after successful application
        discountCampaignsStore.setCodeState('ready');

        // Show the promo text and update button with the active code
        if (promoText) promoText.classList.remove(classes.hidden);
        if (copyButton) {
          copyButton.classList.remove(classes.hidden);
          copyButtonText.innerText = discountCampaignsStore.active.code[0];
          copyButton.disabled = true;
        }
        // Code was successfully applied, open the overlay
        overlaysStore.open({
          ignoreDismissed: true,
          namespace: 'discountCampaignsActivatedModal',
          clearQueue: true,
        });

        unsubscribe();
      } else if (state.codeState === 'invalid' || state.codeState === 'error') {
        cnvs.ReportError('Code application failed:', state.errorType);

        // Reset button text to original
        if (copyButton?.dataset?.text) {
          copyButtonText.innerText = copyButton.dataset.text;
        }

        unsubscribe();
      }
    });
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('campaign-teaser')) {
    return;
  }

  discountCampaignsStore = useDiscountCampaignsStore();
  overlaysStore = useOverlaysStore();

  customElements.define('campaign-teaser', CampaignTeaser);
});
